import {
  UPDATE_RENTAL_FORM_V2_REQUEST,
  CREATE_RENTAL_FORM_V2_SUCCESS,
  FETCH_RENTAL_FORM_V2_SUCCESS,
  FETCH_RENTAL_FORM_V2_REQUEST,
  UPDATE_RENTAL_FORM_V2_SUCCESS,
  RENTAL_FORM_V2_FAILURE,
  TOGGLE_RENTAL_FORM_V2_LOADING,
} from 'Constants/redux';
import {
  TOGGLE_ITEMS_MODAL,
  TOGGLE_ADD_INVENTORY_MODAL,
  TOGGLE_ADD_INVENTORY_TO_BUNDLE_MODAL,
  TOGGLE_TAX_CALCULATED,
  TOGGLE_ADD_SERVICE_MODAL,
  TOGGLE_SERIALIZED_ITEM_DRAWER,
  RESET_RENTAL_FORM_V2,
  SET_RENTAL_FORM_V2_TYPE,
  TOGGLE_NON_DISCOUNTABLES_ALERT_MODAL,
  TOGGLE_RENTAL_FORM_V2_LOADING_PRICING,
  TOGGLE_EVENT_END_DATE_MODAL,
} from '../constants/redux';
import RentalVersion from '../types/RentalVersion';
import { cloneDeep } from '@apollo/client/utilities';
import Inventory from '../components/rentals/form/Inventory';
import { v4 as uuidv4 } from 'uuid';
import RentalVersionData from '../types/RentalVersionData';

const INITIAL_STATE: RentalVersion = {
  id: null,
  rentalId: null,
  data: {},
  loading: true,
  loadingPricing: false,
  isItemsModalOpen: false,
  addInventoryModal: {
    open: false,
    rentalSectionId: '',
  },
  nonDiscountablesAlertModal: {
    open: false,
    kind: 'itemAdded',
    nonDiscountableItems: [],
  },
  addInventoryToBundleModal: {
    open: false,
    rentalSectionId: '',
    rentalBundle: {},
  },
  eventEndDateModal: {
    open: false,
    event: {},
  },
  taxCalculated: true,
  addServiceModal: {
    open: false,
    rentalSectionRvId: '',
    serializedItemDrawer: {
      open: false,
      productId: null,
      product: {} as Inventory,
    },
  },
  type: 'new',
};

// Date attributes on the top-level of the store
const SHALLOW_DATE_ATTRIBUTES = [
  'flipDateTime',
  'setByTimeDateTime',
  'strikeDateTime',
  'customExpireDate',
];

const SCHEDULE_DATE_ATTRIBUTES = [
  'eventEnd',
  'eventEndDateTime',
  'eventEndTime',
  'eventStart',
  'eventStartDateTime',
  'eventStartTime',
  'endDateTime',
  'startDateTime',
  'flipDateTime',
  'setByTimeDateTime',
  'strikeDateTime',
  'checkin',
  'checkinTime',
  'checkinWindowStart',
  'checkout',
  'checkoutTime',
  'checkoutWindowEnd',
  'offShelfAt',
  'onShelfAt',
  'endWindowBeginning',
  'startWindowFinish',
];

const parseDates = (data, dateFields) => {
  Object.entries(data).forEach(([key, value]) => {
    if (dateFields.includes(key) && value !== null) {
      data[key] = new Date(value);
    }
  });
};

const emptyBillingLines = [
  'inventoryTotal',
  'rentalItemTotal',
  'rentalItemStandardTotal',
  'rentalItemTemporaryTotal',
  'purchaseTotal',
  'bundleTotal',
  'staffTotal',
  'deliveryTotal',
  'globalDiscountsTotal',
  'globalFeesTotal',
  'standardFeesTotal',
  'damageWaiverFeeTotal',
  'damageFeesTotal',
  'itemDamageFeesTotal',
  'accessoryDamageFeesTotal',
  'subtotal',
  'taxTotal',
  'salesTaxesTotal',
  'additionalTaxesTotal',
  'creditCardFeeTotal',
  'orderTotal',
].map((key) => ({
  tag: '',
  name: key,
  kind: key,
  baseValue: 0,
  finalValue: 0,
}));

// Merge nested association payloads based on the RV-ID.
// Example: item nested under section membership and rental_sections gets updated.
//   The new payload is merged with the existing, overwriting fields in common and appending new additions.
function mergeArraysByRvId(currentArr: any[] = [], incomingArr: any[] = []) {
  const mergedArr = [...currentArr];

  incomingArr.forEach((item) => {
    const rvId = item.rvId;
    const foundIdx = mergedArr.findIndex(
      (obj) => obj.rvId === rvId && rvId !== undefined
    );
    if (foundIdx !== -1) {
      mergedArr[foundIdx] = { ...mergedArr[foundIdx], ...item };
    } else {
      const newItem = { ...item };
      if (!newItem.rv_id && !newItem.rvId) {
        newItem.rvId = uuidv4();
      }
      mergedArr.push(newItem);
    }
  });

  return mergedArr;
}

const hasOneAssociations = [
  "rentalTeamEmailSettingAttributes",
  "scheduleAttributes",
  "rentalSettingAttributes",
  "venueRentalRelationshipAttributes",
  "companyRentalRelationshipAttributes"
]

function deepMergeWithArrayRvId(
  stateData: Partial<RentalVersionData> & { pricing: any },
  payload: Partial<RentalVersionData>
) {
  const result = { ...stateData };
  Object.entries(payload).forEach(([key, value]) => {
    const typedKey = key as keyof RentalVersionData;

    // Array attributes needs to be merged by RV-ID
    // Custom object-like attributes (i.e., hasOneAssociations) are merged via simple hash operations
    // Primitives, simple values are directly added to the result.
    if (Array.isArray(value) && Array.isArray(stateData[typedKey])) {
      result[typedKey] = mergeArraysByRvId(stateData[typedKey], value);
    } else if (
      hasOneAssociations.includes(key)
    ) {
      result[typedKey] = { ...result[typedKey], ...value };
    } else {
      result[typedKey] = value;
    }
  });
  return result;
}

const RentalFormV2Reducer = (state = cloneDeep(INITIAL_STATE), action) => {
  switch (action.type) {
    case RESET_RENTAL_FORM_V2:
      return cloneDeep(INITIAL_STATE);
    case UPDATE_RENTAL_FORM_V2_REQUEST:
    case FETCH_RENTAL_FORM_V2_REQUEST:
      return {
        ...state,
        loading: true,
      };
    case CREATE_RENTAL_FORM_V2_SUCCESS:
      parseDates(action.payload.rentalVersion.data, SHALLOW_DATE_ATTRIBUTES);

      parseDates(
        action.payload.rentalVersion.data.scheduleAttributes,
        SCHEDULE_DATE_ATTRIBUTES
      );

      return {
        ...state,
        ...action.payload.rentalVersion,
        currentRentalStatus: action.payload.rentalVersion.data.status,
        pricing: action.payload.rentalVersion.pricing || emptyBillingLines,
        loading: false,
        taxCalculated:
          action.payload.rentalVersion.data.deliverySettingAttributes
            .defaultDelivery !== 'customer_pick_up',
      };
    case FETCH_RENTAL_FORM_V2_SUCCESS:
      parseDates(action.payload.rentalVersion.data, SHALLOW_DATE_ATTRIBUTES);

      parseDates(
        action.payload.rentalVersion.data.scheduleAttributes,
        SCHEDULE_DATE_ATTRIBUTES
      );

      return {
        ...state,
        ...action.payload.rentalVersion,
        currentRentalStatus: action.payload.rentalVersion.data.status,
        loading: false,
      };
    case UPDATE_RENTAL_FORM_V2_SUCCESS:
      parseDates(action.payload, SHALLOW_DATE_ATTRIBUTES);

      if (action.payload.scheduleAttributes)
        parseDates(action.payload.scheduleAttributes, SCHEDULE_DATE_ATTRIBUTES);

      // Billing lines should not be merged. It's an exception. Therefore, we make it undefined so the one on "action.payload" is used.
      // Note: We have 2 sources of truth now. The "pricing" at the top-level store, and the "pricing" nested under "data".
      // We should keep only the top-level. The other could be probably deprecated without consequences, but we need to test it thoroughly.
      const mergedData = deepMergeWithArrayRvId({...state.data, pricing: undefined}, action.payload);

      return {
        ...state,
        data: mergedData,
        loading: false,
        ...(action.payload.pricing && { pricing: action.payload.pricing }),
      };
    case TOGGLE_ITEMS_MODAL:
      return {
        ...state,
        isItemsModalOpen: action.status,
      };
    case TOGGLE_ADD_INVENTORY_MODAL:
      return {
        ...state,
        addInventoryModal: {
          ...state.addServiceModal,
          open: action.status,
          rentalSectionId: action.rentalSectionId,
          serializedItemDrawer: {
            open: false,
            productId: null,
            product: {},
          },
        },
      };
    case TOGGLE_RENTAL_FORM_V2_LOADING_PRICING:
      return {
        ...state,
        loadingPricing: action.loadingPricing,
        pricingCancelToken: action.pricingCancelToken,
      }
    case TOGGLE_ADD_INVENTORY_TO_BUNDLE_MODAL:
      return {
        ...state,
        addInventoryToBundleModal: {
          open: action.status,
          rentalSectionId: action.rentalSectionId,
          rentalBundle: action.rentalBundle,
          serializedItemDrawer: {
            open: false,
            productId: null,
            product: {},
          },
        },
      };
    case TOGGLE_TAX_CALCULATED:
      return {
        ...state,
        taxCalculated: action.status,
      };
    case TOGGLE_ADD_SERVICE_MODAL:
      return {
        ...state,
        addServiceModal: {
          open: action.status,
          rentalSectionRvId: action.rentalSectionRvId,
        },
      };
    case TOGGLE_NON_DISCOUNTABLES_ALERT_MODAL:
      return {
        ...state,
        nonDiscountablesAlertModal: {
          open: action.status,
          nonDiscountableItems: action.nonDiscountableItems,
          kind: action.kind,
          onSubmit: action.onSubmit,
        },
      };
    case TOGGLE_EVENT_END_DATE_MODAL:
      return {
        ...state,
        eventEndDateModal: {
          open: action.status,
          event: action.event,
        },
      };
    case TOGGLE_SERIALIZED_ITEM_DRAWER:
      return {
        ...state,
        addInventoryModal: {
          ...state.addInventoryModal,
          serializedItemDrawer: {
            open: action.status,
            productId: action.productId,
            product: action.product,
          },
        },
      };
    case RENTAL_FORM_V2_FAILURE:
      return {
        ...state,
        loading: false,
      };
    case TOGGLE_RENTAL_FORM_V2_LOADING:
      return {
        ...state,
        loading: action.status,
      };
    case SET_RENTAL_FORM_V2_TYPE:
      return {
        ...state,
        type: action.payload,
      };
    default:
      return state;
  }
};

export default RentalFormV2Reducer;
